import { createAction } from '@reduxjs/toolkit'
import { createReducer, createTypes } from 'reduxsauce'
import fetchApi from '../utils/fetch'

//
// ACTIONS
// -----------------------------------------------------------------------------

export const types = createTypes(`
  CERTIFICATES_INITIAL
  CERTIFICATES_SUCCESS
  CERTIFICATES_ERROR
  CERTIFICATES_FETCH_SUCCESS
  CERTIFICATES_FETCH_FAILURE
  CERTIFICATE_CLAIM_SUCCESS
  CERTIFICATE_CLAIM_FAILURE
  CERTIFICATE_GENERAL_FETCH_SUCCESS
  CERTIFICATE_GENERAL_FETCH_FAILURE
  CERTIFICATE_GENERAL_GENERATE_SUCCESS
  CERTIFICATE_GENERAL_GENERATE_FAILURE
`)

export const certificatesStatus = {
  initial: types.CERTIFICATES_INITIAL,
  success: types.CERTIFICATES_SUCCESS,
  error: types.CERTIFICATES_ERROR
}

// The All-Courses certificate (SPEC-040): one per user, regenerable.
// `none` is the expected 404 (never generated), distinct from a load error.
export const generalStatus = {
  initial: 'initial',
  none: 'none',
  success: 'success',
  error: 'error'
}

//
// REDUCER
// -----------------------------------------------------------------------------

export const INITIAL_STATE = {
  status: certificatesStatus.initial,
  // null until the first fetch resolves; then an array of certificates
  // (claimed ones have an id and downloadUrl, claimable completions don't)
  list: null,
  general: {
    status: generalStatus.initial,
    // { id, printedName, generatedAt, courses[], verificationCode, … }
    data: null
  }
}

// The backend list endpoint returns a bare array; fetchHelper spreads it into
// an object with numeric keys, so restore it with Object.values.
export const successList = (state = INITIAL_STATE, action) => ({
  ...state,
  list: Object.values(action.payload),
  status: certificatesStatus.success
})

// Claim responds with the updated certificate; upsert it into the list so the
// claimable row (no id yet) becomes the claimed one without a refetch.
export const successClaim = (state = INITIAL_STATE, action) => ({
  ...state,
  list: (state.list || []).map((certificate) =>
    certificate.tutorial.slug === action.payload.tutorial.slug
      ? action.payload
      : certificate
  ),
  status: certificatesStatus.success
})

export const failure = (state = INITIAL_STATE) => ({
  ...state,
  status: certificatesStatus.error
})

// Fetch and generate both answer with the whole certificate.
export const successGeneral = (state = INITIAL_STATE, action) => ({
  ...state,
  general: { status: generalStatus.success, data: action.payload }
})

// A 404 means the user hasn't generated one yet — an expected state that
// shows the "Get" action. Anything else is a load failure, which must not
// look like "none" (the page would offer to generate over an existing one).
export const failureGeneralFetch = (state = INITIAL_STATE, action) => ({
  ...state,
  general: {
    status:
      action.payload?.status === 404 ? generalStatus.none : generalStatus.error,
    data: null
  }
})

export const HANDLERS = {
  [types.CERTIFICATES_FETCH_SUCCESS]: successList,
  [types.CERTIFICATES_FETCH_FAILURE]: failure,
  [types.CERTIFICATE_CLAIM_SUCCESS]: successClaim,
  [types.CERTIFICATE_GENERAL_FETCH_SUCCESS]: successGeneral,
  [types.CERTIFICATE_GENERAL_FETCH_FAILURE]: failureGeneralFetch,
  [types.CERTIFICATE_GENERAL_GENERATE_SUCCESS]: successGeneral
}

export default createReducer(INITIAL_STATE, HANDLERS)

//
// ACTION CREATORS
// -----------------------------------------------------------------------------

export const certificatesFetchSuccess = createAction(
  types.CERTIFICATES_FETCH_SUCCESS
)
export const certificatesFetchFailure = createAction(
  types.CERTIFICATES_FETCH_FAILURE
)
export const certificatesFetch = fetchApi(
  '/certificates/',
  { method: 'GET' },
  certificatesFetchSuccess,
  certificatesFetchFailure
)

export const certificateClaimSuccess = createAction(
  types.CERTIFICATE_CLAIM_SUCCESS
)
export const certificateClaimFailure = createAction(
  types.CERTIFICATE_CLAIM_FAILURE
)
export const certificateClaim = fetchApi(
  '/certificates/claim/',
  { method: 'POST' },
  certificateClaimSuccess,
  certificateClaimFailure
)

export const certificateGeneralFetchSuccess = createAction(
  types.CERTIFICATE_GENERAL_FETCH_SUCCESS
)
export const certificateGeneralFetchFailure = createAction(
  types.CERTIFICATE_GENERAL_FETCH_FAILURE
)
export const certificateGeneralFetch = fetchApi(
  '/certificates/general/',
  { method: 'GET' },
  certificateGeneralFetchSuccess,
  certificateGeneralFetchFailure
)

export const certificateGeneralGenerateSuccess = createAction(
  types.CERTIFICATE_GENERAL_GENERATE_SUCCESS
)
export const certificateGeneralGenerateFailure = createAction(
  types.CERTIFICATE_GENERAL_GENERATE_FAILURE
)
// Body is {} (name on file) or { name }; 400 { name } means no name on file
export const certificateGeneralGenerate = fetchApi(
  '/certificates/general/',
  { method: 'POST' },
  certificateGeneralGenerateSuccess,
  certificateGeneralGenerateFailure
)
